import { lazy, StrictMode, Suspense } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import EvalSetListPage from './EvalSetListPage.tsx';
import OAuthCallback from './pages/OAuthCallback.tsx';
import SamplesPage from './SamplesPage.tsx';
import ScansPage from './ScansPage.tsx';
import SamplePermalink from './routes/SamplePermalink.tsx';
import LaunchPage from './pages/LaunchPage.tsx';

// Each viewer ships a global stylesheet (Bootstrap plus its own theme
// overrides) that cascades across the whole document. Importing both eagerly
// puts both stylesheets in <head> at once, so each viewer's globals leak
// into the other's pages. Splitting them via React.lazy — combined with the
// full page reloads our cross-viewer links already use — keeps only the
// active viewer's stylesheet loaded.
const EvalPage = lazy(() => import('./EvalPage.tsx'));
const ScanPage = lazy(() => import('./ScanPage.tsx'));

const FallbackRoute = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const logDir = searchParams.get('log_dir');

  if (logDir) {
    // Handle old URL format with log_dir param
    return (
      <Navigate
        replace
        to={{
          pathname: `/eval-set/${encodeURIComponent(logDir)}`,
          hash: location.hash,
        }}
      />
    );
  }

  // Default to eval set list
  return <Navigate replace to="/eval-sets" />;
};

export const AppRouter = () => {
  return (
    <StrictMode>
      <BrowserRouter>
        <Routes>
          {/* OAuth callback must be outside AuthProvider to avoid redirect loops */}
          <Route path="oauth/complete" element={<OAuthCallback />} />
          {/* All other routes require authentication */}
          <Route
            path="*"
            element={
              <AuthProvider>
                <Suspense fallback={null}>
                  <Routes>
                    <Route path="scan/:scanFolder/*" element={<ScanPage />} />
                    <Route
                      path="eval-set/:evalSetId/*"
                      element={<EvalPage />}
                    />
                    <Route path="eval-sets" element={<EvalSetListPage />} />
                    <Route path="samples" element={<SamplesPage />} />
                    <Route path="scans" element={<ScansPage />} />
                    <Route
                      path="permalink/sample/:uuid"
                      element={<SamplePermalink />}
                    />
                    <Route path="launch" element={<LaunchPage />} />
                    <Route path="*" element={<FallbackRoute />} />
                  </Routes>
                </Suspense>
              </AuthProvider>
            }
          />
        </Routes>
      </BrowserRouter>
    </StrictMode>
  );
};
